import { AnalyticsUtils } from '@analytics/utils';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { getAppConfig } from '@core/config-store';
import type { HomeBatteryOrderFormRendering } from '@feh/homebattery-models';
import {
  selectHomebatteryAdviceSubmitState,
  selectHomeBatteryFormDataState,
  selectHomeBatteryFunnelSettings,
  setRedirectUrl,
  submitHomebatteryAdviceForm,
} from '@feh/homebattery-store';
import { HomebatteryOrderFormUiComponent } from '@feh/homebattery-ui';
import { LoadingScreenComponent } from '@feh/shared-ui';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { ScFieldValuePipe } from '@innogy/feh/shared';
import { TrackToolService } from '@analytics/events';
import { Status } from '@essent/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import type { RfaAddress } from '@innogy/feh/rfa';
import * as Sentry from '@sentry/angular';

import {
  createForm,
  createTrackToolConfig,
  mapFormToSubmitPayload,
} from './homebattery-order-form.container.helpers';
import { initializeHomeBatteryFunnelComponent } from '../homebattery-wizard-container/homebattery-wizard.container.helpers';

const analyticsComponentName = 'thuisbatterij adviesformulier';

@Sentry.TraceClass({ name: 'FEIT-Homebattery-Order-Form-Container' })
@Component({
  selector: 'wl-homebattery-order-form-container',
  standalone: true,
  imports: [
    HomebatteryOrderFormUiComponent,
    LoadingScreenComponent,
    ScFieldValuePipe,
    AutomationIdDirective,
  ],
  templateUrl: './homebattery-order-form-container.component.html',
})
export class HomebatteryOrderFormContainerComponent implements OnInit {
  public readonly rendering = input.required<HomeBatteryOrderFormRendering>();
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly trackToolService = inject(TrackToolService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly config = this.store.selectSignal(getAppConfig);
  private readonly wizardFormData = this.store.selectSignal(
    selectHomeBatteryFormDataState
  );
  private readonly funnelSettings = this.store.selectSignal(
    selectHomeBatteryFunnelSettings
  );
  private readonly e2eTrackingId =
    inject(AnalyticsUtils).generateAnalyticsE2EId();

  protected readonly form = computed(() =>
    createForm(this.rendering(), analyticsComponentName)
  );
  protected readonly trackToolConfig = computed(() =>
    createTrackToolConfig(
      analyticsComponentName,
      this.e2eTrackingId,
      this.form()
    )
  );
  protected readonly submitState = this.store.selectSignal(
    selectHomebatteryAdviceSubmitState
  );

  protected readonly showLoader = signal(true);
  protected readonly loadingDuration = 3000;

  protected readonly loaderItems = computed(() => {
    return this.rendering().fields.LoadingScreenItems.map((item) => {
      const fields = item.fields;
      return {
        icon: fields.icon?.fields?.value?.value || 'check-circle',
        text: fields.title?.value || '',
      };
    });
  });

  ngOnInit(): void {
    this.store.dispatch(
      initializeHomeBatteryFunnelComponent(
        'order',
        this.rendering().fields.FunnelSettings
      )
    );

    const successPage = this.funnelSettings().successPage;
    if (successPage) {
      this.store.dispatch(
        setRedirectUrl({ redirectUrl: { value: successPage } })
      );
    }

    this.store
      .select(selectHomebatteryAdviceSubmitState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.status === Status.SUCCESS) {
          this.trackToolService.completeTool(this.trackToolConfig());
        }
      });
  }

  protected get address(): string {
    const addressData = this.wizardFormData().address;
    return addressData
      ? `${addressData?.street} ${addressData?.houseNumber}${addressData?.houseNumberAddition}, ${addressData?.postcode} ${addressData?.city}`
      : '';
  }

  protected submit() {
    this.form().markAllAsTouched();

    if (this.form().valid) {
      this.store.dispatch(
        submitHomebatteryAdviceForm({
          payload: mapFormToSubmitPayload(
            this.form(),
            this.rendering(),
            this.config()?.brand === 'ed' ? 'energiedirect' : 'essent',
            this.router.url,
            this.wizardFormData().address as RfaAddress
          ),
        })
      );
    }
  }
}
